import type { AxiosInstance, AxiosResponse } from "axios";
import type {
  AuditLogDetailResponse,
  AuditLogsFilterOptions,
  AuditLogsFilterOptionsResponse,
  AuditLogsListResponse,
  AuditLogsQueryParams,
} from "../types";

/**
 * The shared `useAxiosAuth0()` instance is hardcoded to `${REACT_APP_API_URL}/v1`.
 * Audit Logs lives under `/api/v2/...`, so every request in this module
 * overrides `baseURL` per call to `${REACT_APP_API_URL}/v2`.
 *
 * Using an absolute URL via `baseURL` (instead of building the full URL
 * inline) keeps the Auth0 request interceptor active.
 */
function getV2BaseUrl(): string {
  const apiBase = process.env.REACT_APP_API_URL || "http://localhost:3000";
  return `${apiBase}/v2`;
}

const AUDIT_LOGS_PATH = "/audit-logs";

/**
 * Strip undefined / null / empty-string values and serialize array values
 * (e.g. `action: ["create", "update"]`) as comma-separated strings — the
 * backend expects CSV for `action`.
 */
function buildQueryParams(
  params: AuditLogsQueryParams | undefined
): Record<string, string | number> | undefined {
  if (!params) return undefined;
  const out: Record<string, string | number> = {};

  (Object.keys(params) as (keyof AuditLogsQueryParams)[]).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      out[key] = value.join(",");
      return;
    }
    out[key] = value as string | number;
  });

  return Object.keys(out).length > 0 ? out : undefined;
}

export function fetchAuditLogs(
  api: AxiosInstance,
  params?: AuditLogsQueryParams
): Promise<AxiosResponse<AuditLogsListResponse>> {
  return api.get<AuditLogsListResponse>(AUDIT_LOGS_PATH, {
    baseURL: getV2BaseUrl(),
    params: buildQueryParams(params),
  });
}

export function fetchAuditLogById(
  api: AxiosInstance,
  id: number | string,
  options?: { tenantId?: number | null }
): Promise<AxiosResponse<AuditLogDetailResponse>> {
  const tenantId = options?.tenantId ?? null;
  return api.get<AuditLogDetailResponse>(`${AUDIT_LOGS_PATH}/${id}`, {
    baseURL: getV2BaseUrl(),
    params: tenantId != null ? { tenantId } : undefined,
  });
}

/**
 * Module-level cache for `/filters/options`. Backend dropdowns rarely change
 * within a single tenant, so the brief asks for a ~10min stale window.
 *
 * The cache is keyed by `(tenantId, productId)` because:
 *   - tenantId — products / resourceTypes available depend on the tenant
 *     (a partner viewing a specific customer may see a smaller catalogue
 *     than the partner aggregate view).
 *   - productId — when the UI filters by a specific product, the backend
 *     returns ONLY the resource types belonging to that product. Different
 *     productIds therefore produce different responses and need their own
 *     cache slots.
 */
const FILTER_OPTIONS_TTL_MS = 10 * 60 * 1000;

interface FilterOptionsCacheEntry {
  data: AuditLogsFilterOptions;
  fetchedAt: number;
}

const filterOptionsCache = new Map<string, FilterOptionsCacheEntry>();
const filterOptionsInflight = new Map<string, Promise<AuditLogsFilterOptions>>();

function cacheKey(
  tenantId: number | null | undefined,
  productId: number | null | undefined
): string {
  const t = tenantId == null ? "__token__" : String(tenantId);
  const p = productId == null ? "__all__" : String(productId);
  return `${t}::${p}`;
}

export interface FetchFilterOptionsOptions {
  forceRefresh?: boolean;
  /** Tenant scope — see `AuditLogsQueryParams.tenantId` for semantics. */
  tenantId?: number | null;
  /**
   * Optional product scope. When set, the backend narrows the
   * `resourceTypes` array to the types belonging to that product
   * (e.g. picking "Kontrol Edge" hides cluster / bgp_neighbor / ...).
   * `products`, `actions` and `sources` are unchanged.
   */
  productId?: number | null;
}

export function fetchAuditLogsFilterOptions(
  api: AxiosInstance,
  options?: FetchFilterOptionsOptions
): Promise<AuditLogsFilterOptions> {
  const tenantId = options?.tenantId ?? null;
  const productId = options?.productId ?? null;
  const key = cacheKey(tenantId, productId);
  const now = Date.now();
  const cached = filterOptionsCache.get(key);
  const cacheValid = cached != null && now - cached.fetchedAt < FILTER_OPTIONS_TTL_MS;

  if (!options?.forceRefresh && cacheValid && cached) {
    return Promise.resolve(cached.data);
  }

  const existingInflight = filterOptionsInflight.get(key);
  if (existingInflight && !options?.forceRefresh) {
    return existingInflight;
  }

  const params: Record<string, number> = {};
  if (tenantId != null) params.tenantId = tenantId;
  if (productId != null) params.productId = productId;

  const inflight = api
    .get<AuditLogsFilterOptionsResponse>(`${AUDIT_LOGS_PATH}/filters/options`, {
      baseURL: getV2BaseUrl(),
      params: Object.keys(params).length > 0 ? params : undefined,
    })
    .then((response) => {
      const body = response.data;
      if (!body?.success || !body.data) {
        throw new Error(body?.message ?? "Failed to load filter options");
      }
      filterOptionsCache.set(key, { data: body.data, fetchedAt: Date.now() });
      return body.data;
    })
    .finally(() => {
      filterOptionsInflight.delete(key);
    });

  filterOptionsInflight.set(key, inflight);
  return inflight;
}

/** Test-only / hard-reset utility (e.g. when switching tenants). */
export function clearAuditLogsFilterOptionsCache(): void {
  filterOptionsCache.clear();
  filterOptionsInflight.clear();
}
